import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Card from '../../../components/card/Card';
import Table from '../../../components/table/table';
import Badge from '../../../components/badge/Badge';
import P from '../../../components/texts/p/P';
import Span from '../../../components/texts/span/Span';
import H from '../../../components/texts/h/H';
import Icon from '../../../components/icon/Icon';
import { indicator } from '../../../types/interfaces';
import { RISK_STATUS } from '../../../utils/Constants';
import type { RiskIndicatorsUiSettings } from '../../../services/TenantUiService';
import {
  DEFAULT_ATLANTIC_GROUP_TITLES,
  groupIndicatorsForAtlantic,
} from './atlanticBpbGroupMap';
import {
  GROUP_ORDER,
  GROUP_PREDICTOR_ALTMAN,
  GROUP_PREDICTOR_SPRINGATE,
  GROUP_RATIOS_POST_BALANCE,
  GROUP_TITLES,
  GROUP_UNCATEGORIZED,
  bpbCodeToSortKey,
  resolveBpbIndicatorGroup,
} from './bpbIndicatorCategoryGroup';

/** Mismo ancho para SEGURO/REVISAR/RIESGO ALTO (alineado con FinancialIndicatorsGroupTable). */
const StatusBadgeCell = styled.div`
  width: 110px;
  min-width: 110px;
  max-width: 110px;
  box-sizing: border-box;
  display: inline-flex;
  justify-content: center;
  align-items: center;

  /* Mismo ancho que RIESGO ALTO: el badge ocupa siempre el 100% de la celda */
  & > * {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const GroupHeader = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
`;

const GroupTitle = styled(H)`
  margin: 0;
`;

/** Grid de KPIs (indicadores sin `category`): 3 columnas en desktop, menos en pantallas chicas. */
export function getKpiSectionGridTemplateColumns(windowWidth: number): string {
  if (windowWidth < 576) return '1fr';
  if (windowWidth < 900) return 'repeat(2, minmax(0, 1fr))';
  return 'repeat(3, minmax(0, 1fr))';
}

function groupIndicators(
  current: indicator[],
  previous: indicator[]
): Map<string, { current: indicator[]; previous: indicator[] }> {
  const byCode = (arr: indicator[]) => new Map(arr.map((i) => [i.code, i]));
  const currentByCode = byCode(current);
  const previousByCode = byCode(previous);
  const allCodes = new Set([...currentByCode.keys(), ...previousByCode.keys()]);
  const byGroup = new Map<string, { current: indicator[]; previous: indicator[] }>();
  for (const code of allCodes) {
    const rowSample = currentByCode.get(code) ?? previousByCode.get(code);
    const cat = rowSample?.category;
    const group = resolveBpbIndicatorGroup(cat, code);
    if (!byGroup.has(group)) byGroup.set(group, { current: [], previous: [] });
    const cur = currentByCode.get(code);
    const prev = previousByCode.get(code);
    if (cur) byGroup.get(group)!.current.push(cur);
    if (prev) byGroup.get(group)!.previous.push(prev);
  }
  for (const g of byGroup.values()) {
    g.current.sort((a, b) => bpbCodeToSortKey(a.code) - bpbCodeToSortKey(b.code));
    g.previous.sort((a, b) => bpbCodeToSortKey(a.code) - bpbCodeToSortKey(b.code));
  }
  return byGroup;
}

function usesAtlanticGrouping(uiSettings?: RiskIndicatorsUiSettings | null): boolean {
  return Array.isArray(uiSettings?.groupOrder) && uiSettings!.groupOrder!.length > 0;
}

// Props for the DualBalanceIndicators component
interface DualBalanceIndicatorsProps {
  indicators: indicator[];
  previousIndicators?: indicator[];
  previousPeriodLabel: string;
  currentPeriodLabel: string;
  /** When false (Movil / flag OFF), tables show only the current period column. Default true. */
  comparePreviousPeriod?: boolean;
  /** Tenant UI scope `risk.indicators`. Absent/null → legacy grouping + columns. */
  uiSettings?: RiskIndicatorsUiSettings | null;
  /** BPB display order by code (composed at runtime by BFF). */
  indicatorOrder?: Record<string, number>;
}

// Window size hook
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

// Helper to get badge status variant
const getBadgeStatus = (status: string) => {
  const mapStatus = new Map();
  mapStatus.set(RISK_STATUS.approved.label, 'success');
  mapStatus.set(RISK_STATUS.revision.label, 'warning');
  mapStatus.set(RISK_STATUS.medium_risk.label, 'warning');
  mapStatus.set(RISK_STATUS.high_risk.label, 'danger');
  return mapStatus.get(status) ? mapStatus.get(status) : 'success';
};

// Helper to get status text
const getStatusText = (statusCode: string) => {
  const status = Object.values(RISK_STATUS).find(
    (item) => item.label === statusCode
  );
  return status ? status.text : 'Estado desconocido';
};

// Helper to convert string with comma decimals to number
const toNumber = (value: string): number => {
  if (!value) return NaN;
  return parseFloat(value.replace(/\./g, '').replace(',', '.'));
};

/**
 * Convierte texto numérico BPB a número sin usar `parseFloat(x) || …` (0 es válido, no falsy).
 * Con coma → `toNumber` (es-AR); sin coma → `parseFloat` (p. ej. valores Kotlin con punto).
 */
function parseBpbDecimalString(raw: string): number {
  const s = raw.trim();
  if (!s) return NaN;
  if (s.includes(',')) {
    const n = toNumber(s);
    return Number.isNaN(n) ? NaN : n;
  }
  const n = parseFloat(s);
  return Number.isNaN(n) ? NaN : n;
}

// Helper to format indicator value - consistent with ChecklightResume.tsx
const formatBpbIndicatorValue = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') return '-';
  const s = String(value).trim();
  
  // Convert legacy labels to N/A
  if (['Sin ventas', 'Sin deudas', 'Sin patrimonio neto'].includes(s)) {
    return 'N/A';
  }
  if (/^Sin |^N\/A$/i.test(s)) {
    return 'N/A';
  }
  
  // Handle percentages
  if (s.endsWith('%')) {
    const num = parseBpbDecimalString(s.slice(0, -1));
    return Number.isNaN(num) ? s : `${Math.round(num)}%`;
  }
  
  // Handle currency
  if (s.includes('$')) {
    const neg = /^-/.test(s);
    const numStr = s.replace(/^-\$?\s*|\$?\s*/g, '').replace(/\s/g, '');
    const num = numStr.includes(',') ? toNumber(numStr) : parseFloat(numStr);
    if (Number.isNaN(num)) return s;
    const formatted = Math.round(num).toLocaleString('es-AR', { maximumFractionDigits: 0 });
    return (neg ? '-$ ' : '$ ') + formatted;
  }
  
  // Handle regular numbers
  const num = parseBpbDecimalString(s);
  if (Number.isNaN(num)) return s;
  return num.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
};

const roundInteger = (v: string) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? formatBpbIndicatorValue(v) : String(Math.round(n));
};

const roundYears = (v: string) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? formatBpbIndicatorValue(v) : `${Math.round(n)} años`;
};

const PERCENT_RATIO_CODES = new Set([
  'BPB_029',
  'BPB_018',
  'BPB_019',
  'BPB_020',
  'BPB_040',
  'BPB_047',
  'BPB_048',
  'BPB_063',
]);

/** Amount ceilings: $ + integer es-AR. */
const CURRENCY_AMOUNT_CODES = new Set([
  'BPB_004',
  'BPB_013',
  'BPB_037',
  'BPB_041',
  'BPB_042',
  'BPB_043',
  'BPB_044',
  'BPB_045',
  'BPB_046',
  'BPB_049',
  'BPB_051',
  'BPB_052',
  'BPB_053',
  'BPB_054',
  'BPB_058',
  'BPB_059',
  'BPB_060',
]);

/** Backend envía ratios decimales; se muestran como % entero (p. ej. 0.1 → 10%). */
function formatBpbIndicatorValueForCode(
  value: string | number | undefined,
  code: string | null | undefined
): string {
  if (code && CURRENCY_AMOUNT_CODES.has(code)) {
    if (value === undefined || value === null || value === '') return '-';
    const raw = String(value).trim();
    if (['Sin ventas', 'Sin deudas', 'Sin patrimonio neto'].includes(raw)) return 'N/A';
    if (/^Sin |^N\/A$/i.test(raw)) return 'N/A';
    if (raw.includes('$')) return formatBpbIndicatorValue(value);
    const num = parseBpbDecimalString(raw);
    if (Number.isNaN(num)) return formatBpbIndicatorValue(value);
    const neg = num < 0;
    const formatted = Math.round(Math.abs(num)).toLocaleString('es-AR', { maximumFractionDigits: 0 });
    return (neg ? '-$ ' : '$ ') + formatted;
  }

  if (!code || !PERCENT_RATIO_CODES.has(code)) return formatBpbIndicatorValue(value);

  if (value === undefined || value === null || value === '') return '-';
  const raw = String(value).trim();

  if (['Sin ventas', 'Sin deudas', 'Sin patrimonio neto'].includes(raw)) return 'N/A';
  if (/^Sin |^N\/A$/i.test(raw)) return 'N/A';
  if (raw.endsWith('%')) return formatBpbIndicatorValue(value);

  const num = parseBpbDecimalString(raw);
  if (Number.isNaN(num)) return formatBpbIndicatorValue(value);

  const percentPoints = Math.round(num * 100);
  return `${percentPoints}%`;
}

const DualBalanceIndicators: React.FC<DualBalanceIndicatorsProps> = ({
  indicators,
  previousIndicators,
  previousPeriodLabel,
  currentPeriodLabel,
  comparePreviousPeriod = true,
  uiSettings = null,
  indicatorOrder,
}) => {
  const windowWidth = useWindowSize();
  const atlanticMode = usesAtlanticGrouping(uiSettings);
  const groupOrderKeys = useMemo(
    () => (atlanticMode ? (uiSettings!.groupOrder as string[]) : [...GROUP_ORDER]),
    [atlanticMode, uiSettings]
  );
  const groupTitles = useMemo(() => {
    if (!atlanticMode) return GROUP_TITLES;
    return {
      ...DEFAULT_ATLANTIC_GROUP_TITLES,
      ...(uiSettings?.groupTitles ?? {}),
    };
  }, [atlanticMode, uiSettings]);
  const forceSingleColumn = atlanticMode && uiSettings?.columns === 1;

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [expandedHelp, setExpandedHelp] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      for (const k of groupOrderKeys) {
        if (next[k] === undefined) next[k] = true;
      }
      return next;
    });
  }, [groupOrderKeys]);

  const grouped = useMemo(() => {
    if (atlanticMode) {
      return groupIndicatorsForAtlantic(
        indicators,
        previousIndicators ?? [],
        groupOrderKeys,
        indicatorOrder,
      );
    }
    return groupIndicators(indicators, previousIndicators ?? []);
  }, [indicators, previousIndicators, atlanticMode, groupOrderKeys, indicatorOrder]);

  // Helper to get previous indicator value by code (soporta code o risk_indicator_code por si la API envía snake_case)
  const getPrevIndicatorValue = (row: indicator): string => {
    if (!previousIndicators || previousIndicators.length === 0) return '-';
    const code = row.code ?? (row as any).risk_indicator_code ?? '';
    if (!code) return '-';
    const prevIndicator = previousIndicators.find(
      (ind) =>
        (ind.code ?? (ind as any).risk_indicator_code) === code
    );
    const value = prevIndicator?.value ?? (prevIndicator as any)?.value;
    return value !== undefined && value !== null && value !== ''
      ? formatBpbIndicatorValueForCode(value, code)
      : '-';
  };

  const getPrevValueByIndexOrCode = (row: indicator, _rowIndex: number) => getPrevIndicatorValue(row);

  const toggleGroup = (groupKey: string) => {
    setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const toggleHelp = (code: string) => {
    setExpandedHelp((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  // Render table for a group of indicators (baseIndex = índice global del primer elemento de esta tabla)
  // colLabels permite sobrescribir los headers de las columnas de valor (usado en GROUP_RATIOS_POST_BALANCE)
  const renderIndicatorTable = (
    tableIndicators: indicator[],
    tableKey: string,
    baseIndex: number,
    colLabels?: { col1: string; col2: string }
  ) => {
    if (tableIndicators.length === 0) return null;

    const col1Header = colLabels?.col1 ?? currentPeriodLabel;
    const col2Header = colLabels?.col2 ?? previousPeriodLabel;
    const cardWidth = forceSingleColumn || windowWidth < 1200 ? '100%' : '49%';

    const valueColumns = [
      {
        name: col1Header,
        selector: (row: indicator) => (
          <Span type='3' variant='neutral'>
            {formatBpbIndicatorValueForCode(
              row.value,
              row.code ?? (row as { risk_indicator_code?: string }).risk_indicator_code
            )}
          </Span>
        ),
        grow: 1.2,
      },
      ...(comparePreviousPeriod
        ? [
            {
              name: col2Header,
              selector: (row: indicator, index: number) => {
                const code = row.code ?? (row as { risk_indicator_code?: string }).risk_indicator_code ?? '';
                const isRatiosPostBalanceLayout = !!colLabels;
                if (
                  !isRatiosPostBalanceLayout &&
                  (code === 'BPB_005' || code === 'BPB_006')
                ) {
                  return <Span type='3' variant='neutral'>-</Span>;
                }
                return (
                  <Span type='3' variant='neutral'>
                    {getPrevValueByIndexOrCode(row, baseIndex + index)}
                  </Span>
                );
              },
              grow: 1.2,
            },
          ]
        : []),
    ];

    return (
      <Card key={tableKey} width={cardWidth}>
        <Table
          columns={[
            {
              name: 'Indicador',
              cell: (row: indicator) => {
                const help = row.help_text?.trim();
                const code = row.code ?? '';
                const expanded = !!expandedHelp[code];
                return (
                  <Card display="flex" flexDirection="column" gap="4px" width="100%">
                    <Card display="flex" alignItems="flex-start" gap="6px" width="100%">
                      <P
                        type="3"
                        variant="neutral"
                        withMargin={false}
                        style={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          width: '100%',
                          flex: 1,
                        }}
                      >
                        {row.description}
                      </P>
                      {help ? (
                        <button
                          type="button"
                          aria-label={expanded ? 'Ocultar ayuda' : 'Mostrar ayuda'}
                          aria-expanded={expanded}
                          data-testid={`help-text-toggle-${code}`}
                          onClick={() => toggleHelp(code)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        >
                          <Icon
                            name={expanded ? 'chevron_up_outline' : 'chevron_down_outline'}
                            variant="neutral"
                            modifier="strong"
                            width={16}
                            height={16}
                            cursor="pointer"
                          />
                        </button>
                      ) : null}
                    </Card>
                    {help && expanded ? (
                      <div data-testid={`help-text-content-${code}`}>
                        <P
                          type="4"
                          variant="neutral"
                          withMargin={false}
                          style={{ whiteSpace: 'normal' }}
                        >
                          {help}
                        </P>
                      </div>
                    ) : null}
                  </Card>
                );
              },
              grow: 2,
            },
            ...valueColumns,
            {
              name: '',
              width: '110px',
              minWidth: '110px',
              maxWidth: '110px',
              selector: (row: indicator) => (
                <StatusBadgeCell>
                  {row.status ? (
                    <Badge variant={getBadgeStatus(row.status)} type='text' size='sm'>
                      {getStatusText(row.status)}
                    </Badge>
                  ) : (
                    <Span type='3' variant='neutral' withMargin={false}>-</Span>
                  )}
                </StatusBadgeCell>
              ),
            },
          ]}
          tableData={{ size: tableIndicators.length, totalElements: tableIndicators.length }}
          data={tableIndicators}
          variant='weak'
          disablePagination
        />
      </Card>
    );
  };

  // Formatters específicos por código para la sección KPI
  const KPI_VALUE_FORMATTERS: Record<string, (v: string) => string> = {
    BPB_005: roundInteger,
    BPB_006: roundInteger,
    BPB_035: roundYears,
  };

  const formatKpiValue = (ind: indicator): string => {
    const formatter = KPI_VALUE_FORMATTERS[ind.code ?? ''];
    if (formatter && ind.value != null) return formatter(String(ind.value));
    const code = ind.code ?? (ind as { risk_indicator_code?: string }).risk_indicator_code;
    return formatBpbIndicatorValueForCode(ind.value, code);
  };

  // Render KPI grid for indicators with null/empty category — siempre visible, no colapsable
  const renderKpiSection = (kpiIndicators: indicator[]) => {
    if (kpiIndicators.length === 0) return null;
    return (
      <div data-testid="dual-balance-kpi-section">
        <Card
          margin={{ top: '3' }}
          display='grid'
          gap='20px'
          style={{
            gridTemplateColumns: getKpiSectionGridTemplateColumns(windowWidth),
            alignItems: 'flex-start',
            columnGap: '24px',
          }}
        >
        {kpiIndicators.map((ind) => (
          <Card key={ind.code} display='flex' flexDirection='column' gap='6px'>
            <P type='4' variant='neutral-1' withMargin={false} weight='bold'>
              {ind.description}
            </P>
            <Card display='flex' alignItems='flex-start' flexWrap='wrap' gap='6px' style={{ rowGap: '2px' }}>
              <P type='2' variant='neutral-1' weight='bold' withMargin={false}>
                {formatKpiValue(ind)}
              </P>
              {ind.status && (
                <Badge variant={getBadgeStatus(ind.status)} type='text' size='sm'>
                  {getStatusText(ind.status)}
                </Badge>
              )}
            </Card>
          </Card>
        ))}
      </Card>
    </div>
    );
  };
  
  if (indicators.length === 0) {
    return (
      <Card margin={{ top: '3' }}>
        <P type='3' variant='neutral'>
          No hay indicadores de balance disponibles para mostrar en el informe.
        </P>
      </Card>
    );
  }

  const kpiIndicators = grouped.get(GROUP_UNCATEGORIZED)?.current ?? [];

  return (
    <Card margin={{ top: '3' }}>
      {kpiIndicators.length > 0 && renderKpiSection(kpiIndicators)}
      {groupOrderKeys.map((groupKey) => {
        const g = grouped.get(groupKey);
        if (!g || (g.current.length === 0 && g.previous.length === 0)) return null;
        const currentList = g.current.length > 0 ? g.current : g.previous;
        const title = groupTitles[groupKey] ?? groupKey;
        const open = openGroups[groupKey] !== false;

        const isSingleColumn =
          forceSingleColumn ||
          groupKey === GROUP_PREDICTOR_ALTMAN ||
          groupKey === GROUP_PREDICTOR_SPRINGATE ||
          groupKey === GROUP_RATIOS_POST_BALANCE;
        const leftList = isSingleColumn ? currentList : currentList.slice(0, Math.ceil(currentList.length / 2));
        const rightList = isSingleColumn ? [] : currentList.slice(Math.ceil(currentList.length / 2));

        const postBalanceColLabels =
          !atlanticMode && groupKey === GROUP_RATIOS_POST_BALANCE
            ? { col1: 'Post Balance', col2: currentPeriodLabel }
            : undefined;

        return (
          <Card key={groupKey} margin={{ top: '4' }}>
            <div data-testid={`dual-balance-group-${groupKey}`}>
            <GroupHeader type="button" onClick={() => toggleGroup(groupKey)}>
              <GroupTitle type="2" variant="neutral-1" weight="bold" withMargin={false}>
                {title}
              </GroupTitle>
              <Icon
                name={open ? 'chevron_up_outline' : 'chevron_down_outline'}
                variant="neutral"
                modifier="strong"
                width={20}
                height={20}
                cursor="pointer"
              />
            </GroupHeader>
            {open && (
              <Card
                margin={{ top: '3' }}
                display="flex"
                width="100%"
                gap="16px"
                flexWrap={forceSingleColumn || windowWidth < 1200 ? 'wrap' : 'nowrap'}
              >
                {renderIndicatorTable(leftList, `${groupKey}-left`, 0, postBalanceColLabels)}
                {rightList.length > 0 && renderIndicatorTable(rightList, `${groupKey}-right`, leftList.length, postBalanceColLabels)}
              </Card>
            )}
            </div>
          </Card>
        );
      })}
    </Card>
  );
};

export default DualBalanceIndicators;
